import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { createTheme, ThemeProvider, CssBaseline } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Layout Components
import Layout from "./components/Layout";
import SuperAdminRoute from "./components/SuperAdminRoute";
import AccessibilityWidget from "./components/AccessibilityWidget";

import { Login, ActivateAccount, SecuritySetup } from "./pages/auth";
import { Dashboard, Analytics } from "./pages/dashboard";
import { OrdersList, OrderDetail, OrderCreate } from "./pages/orders";
import { PatientsList, PatientDetail } from "./pages/patients";
import {
  RoutesList,
  RouteEdit,
  RouteOptimization,
  RouteDetail,
} from "./pages/routes";
import {
  MedicalProfessionalsList,
  MedicalProfessionalDetail,
  CreateMedicalProfessional,
  EditMedicalProfessional,
  MedicalProfessionalDashboard,
  MedicalProfessionalOrderDetail,
} from "./pages/medical-professionals";
import {
  FacilitiesList,
  FacilityDetail,
  EditFacility,
  CreateFacility,
} from "./pages/facilities";
import { UserManagement } from "./pages/user-management";
import { Profile, Settings } from "./pages/settings";
import { NotFound, LandingPage } from "./pages/shared";

// Modern calm medical theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#0369A1",
      light: "#38BDF8",
      dark: "#075985",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#0891B2",
      light: "#67E8F9",
      dark: "#155E75",
    },
    success: {
      main: "#059669",
      light: "#6EE7B7",
      dark: "#047857",
    },
    warning: {
      main: "#D97706",
      light: "#FCD34D",
      dark: "#B45309",
    },
    error: {
      main: "#DC2626",
      light: "#FCA5A5",
      dark: "#B91C1C",
    },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1E293B",
      secondary: "#64748B",
    },
    divider: "#E2E8F0",
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h5: { fontWeight: 600, letterSpacing: "-0.01em" },
    h6: { fontWeight: 600, letterSpacing: "-0.01em" },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, color: "#64748B" },
    body2: { color: "#475569" },
    button: { fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 10,
          padding: "8px 20px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 2px 8px rgba(3, 105, 161, 0.15)",
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(3, 105, 161, 0.2)",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
          border: "1px solid #F1F5F9",
        },
        elevation1: {
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
        },
        elevation3: {
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.02)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          border: "1px solid #F1F5F9",
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            backgroundColor: "#FFFFFF",
            "& fieldset": {
              borderColor: "#E2E8F0",
            },
            "&:hover fieldset": {
              borderColor: "#94A3B8",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#0369A1",
              borderWidth: "1.5px",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 600,
            color: "#475569",
            backgroundColor: "#F8FAFC",
            borderBottom: "2px solid #E2E8F0",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: "#F1F5F9",
          padding: "12px 16px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#F1F5F9",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        },
      },
    },
  },
});

// Loading Component
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "18px",
    }}
  >
    Loading...
  </div>
);

// FIXED: Global Auth Guard Component
const AuthGuard = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {

    // Skip auth check for public routes
    const publicRoutes = ["/login", "/activate", "/security-setup", "/"];
    if (publicRoutes.includes(location.pathname)) {
      return;
    }

    // If not loading and not authenticated, redirect to login
    if (!loading && !isAuthenticated) {
      navigate("/login", {
        replace: true,
        state: { from: location.pathname },
      });
    }
  }, [isAuthenticated, loading, user, navigate, location.pathname]);

  return children;
};

// FIXED: Protected Route component with better error handling
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// NEW: Medical Professional Route - only allows medical professionals
const MedicalProfessionalRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "medical_professional") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// NEW: Non-Medical Professional Route - blocks medical professionals from certain pages
const NonMedicalProfessionalRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === "medical_professional") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// FIXED: Admin Only Route component with better permission checking
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block medical professionals from admin routes
  if (user?.role === "medical_professional") {
    return <Navigate to="/dashboard" replace />;
  }

  // FIXED: Use isAdmin as boolean, not function
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// FIXED: Facility Route component (for facility-specific access)
const FacilityRoute = ({ children, requireSuperAdmin = false }) => {
  const { isAuthenticated, loading, canViewFacility, isSuperAdmin, user } =
    useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block medical professionals from facility routes
  if (user?.role === "medical_professional") {
    return <Navigate to="/dashboard" replace />;
  }

  // FIXED: Use isSuperAdmin as boolean, not function
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // FIXED: Use canViewFacility as boolean, not function
  if (!requireSuperAdmin && !canViewFacility) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// FIXED: Smart Login Route - redirect if already authenticated
const LoginRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // Redirect to intended destination or dashboard
    const from = location.state?.from || "/dashboard";
    return <Navigate to={from} replace />;
  }

  return children;
};

// FIXED: Main App Routes component with AuthGuard
const AppRoutes = () => {
  return (
    <AuthGuard>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />

        {/* FIXED: Smart login route */}
        <Route
          path="/login"
          element={
            <LoginRoute>
              <Login />
            </LoginRoute>
          }
        />

        {/* Public Activation Route (no auth required) */}
        <Route path="/activate" element={<ActivateAccount />} />
        <Route path="/security-setup" element={<SecuritySetup />} />

        {/* Root redirect to dashboard for authenticated users */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout>
                <Navigate to="/dashboard" replace />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Dashboard - Role-based routing */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <RoleDashboard />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Medical Professional Order Detail - Only for medical professionals */}
        <Route
          path="/medical-professional/orders/:orderId"
          element={
            <MedicalProfessionalRoute>
              <Layout>
                <MedicalProfessionalOrderDetail />
              </Layout>
            </MedicalProfessionalRoute>
          }
        />

        {/* Analytics - Admin only, not for medical professionals */}
        <Route
          path="/analytics"
          element={
            <AdminRoute>
              <Layout>
                <Analytics />
              </Layout>
            </AdminRoute>
          }
        />

        {/* User Management - Super Admin only */}
        <Route
          path="/user-management"
          element={
            <SuperAdminRoute>
              <Layout>
                <UserManagement />
              </Layout>
            </SuperAdminRoute>
          }
        />

        {/* =============================================== */}
        {/* FACILITY MANAGEMENT ROUTES - Not for MPs */}
        {/* =============================================== */}
        <Route
          path="/facilities"
          element={
            <FacilityRoute>
              <Layout>
                <FacilitiesList />
              </Layout>
            </FacilityRoute>
          }
        />

        <Route
          path="/facilities/create"
          element={
            <FacilityRoute requireSuperAdmin={true}>
              <Layout>
                <CreateFacility />
              </Layout>
            </FacilityRoute>
          }
        />

        <Route
          path="/facilities/:id"
          element={
            <FacilityRoute>
              <Layout>
                <FacilityDetail />
              </Layout>
            </FacilityRoute>
          }
        />

        <Route
          path="/facilities/:id/edit"
          element={
            <FacilityRoute>
              <Layout>
                <EditFacility />
              </Layout>
            </FacilityRoute>
          }
        />

        {/* =============================================== */}
        {/* ROUTE MANAGEMENT ROUTES - Not for MPs */}
        {/* =============================================== */}
        <Route
          path="/routes"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <RoutesList />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        <Route
          path="/routes/optimize"
          element={
            <AdminRoute>
              <Layout>
                <RouteOptimization />
              </Layout>
            </AdminRoute>
          }
        />

        <Route
          path="/routes/:id"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <RouteDetail />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        <Route
          path="/routes/:id/edit"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <RouteEdit />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        {/* =============================================== */}
        {/* ORDER MANAGEMENT ROUTES - Not for MPs */}
        {/* =============================================== */}
        <Route
          path="/orders"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <OrdersList />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        <Route
          path="/orders/create"
          element={
            <AdminRoute>
              <Layout>
                <OrderCreate />
              </Layout>
            </AdminRoute>
          }
        />

        <Route
          path="/orders/:id"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <OrderDetail />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        {/* =============================================== */}
        {/* PATIENT MANAGEMENT ROUTES - Not for MPs */}
        {/* =============================================== */}
        <Route
          path="/patients"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <PatientsList />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        <Route
          path="/patients/new"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <PatientDetail />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        <Route
          path="/patients/:id"
          element={
            <NonMedicalProfessionalRoute>
              <Layout>
                <PatientDetail />
              </Layout>
            </NonMedicalProfessionalRoute>
          }
        />

        <Route
          path="/patients/:id/edit"
          element={
            <AdminRoute>
              <Layout>
                <PatientDetail />
              </Layout>
            </AdminRoute>
          }
        />

        {/* =============================================== */}
        {/* MEDICAL PROFESSIONALS ROUTES - Admin only */}
        {/* =============================================== */}
        <Route
          path="/medical-professionals"
          element={
            <AdminRoute>
              <Layout>
                <MedicalProfessionalsList />
              </Layout>
            </AdminRoute>
          }
        />

        <Route
          path="/medical-professionals/create"
          element={
            <AdminRoute>
              <Layout>
                <CreateMedicalProfessional />
              </Layout>
            </AdminRoute>
          }
        />

        <Route
          path="/medical-professionals/:id"
          element={
            <AdminRoute>
              <Layout>
                <MedicalProfessionalDetail />
              </Layout>
            </AdminRoute>
          }
        />

        <Route
          path="/medical-professionals/:id/edit"
          element={
            <AdminRoute>
              <Layout>
                <EditMedicalProfessional />
              </Layout>
            </AdminRoute>
          }
        />

        {/* =============================================== */}
        {/* SETTINGS & PROFILE ROUTES */}
        {/* =============================================== */}

        {/* Profile - All authenticated users */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Settings - Admin only, not for medical professionals */}
        <Route
          path="/settings"
          element={
            <AdminRoute>
              <Layout>
                <Settings />
              </Layout>
            </AdminRoute>
          }
        />

        {/* =============================================== */}
        {/* 404 ERROR ROUTE */}
        {/* =============================================== */}

        {/* 404 - All authenticated users */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Layout>
                <NotFound />
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthGuard>
  );
};

// NEW: Role-based dashboard component
const RoleDashboard = () => {
  const { user } = useAuth();

  // Medical professionals see their special dashboard
  if (user?.role === "medical_professional") {
    return <MedicalProfessionalDashboard />;
  }

  // All other users see the regular dashboard
  return <Dashboard />;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AccessibilityWidget />
        <Router>
          <AppRoutes />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
